import _ from 'lodash';

// Instagram paths that are not profiles, so their first segment is not a handle
const NON_PROFILE_PATHS = [
  'p', 'reel', 'reels', 'tv', 'explore', 'accounts', 'direct', 'about', 'legal',
  'developer', 'web', 'challenge', 'emails', 'privacy', 's', 'share',
];

// Instagram paths whose second segment is the handle
const HANDLE_PREFIX_PATHS = ['stories', '_u'];

// Splits a URL into host and path segments. A regex rather than new URL() so it behaves
// the same in every browser and in the test runner.
const parseUrl = input => {
  const match = input.match(/^(?:[a-z]+:)?(?:\/\/)?([^/?#]*)([^?#]*)/i);
  return {
    // Drops any user@ and :port so only the host name is compared
    host: _.toLower(_.get(match, '[1]', '')).replace(/^.*@/, '').replace(/:\d+$/, ''),
    pathParts: _.compact(_.split(_.get(match, '[2]', ''), '/')),
  };
};

// instagram.com or a subdomain of it - not notinstagram.com or instagram.com.evil.com
const isInstagramHost = host => /(^|\.)instagram\.com$/.test(host);

// Story highlights live under /stories/highlights/<id>, which has no handle in it
const isHighlightLink = pathParts =>
  _.toLower(_.head(pathParts)) === 'stories' && _.toLower(_.nth(pathParts, 1)) === 'highlights';

/**
 * Tells whether the input is a link to an Instagram post, reel or other non-profile page
 * @param {string} input - CSV cell value
 * @returns {boolean}
 */
export const isInstagramNonProfileUrl = input => {
  if (!input || !_.isString(input) || !_.includes(_.toLower(input), 'instagram.com')) {
    return false;
  }

  const { host, pathParts } = parseUrl(_.trim(input));
  return isInstagramHost(host)
    && (_.includes(NON_PROFILE_PATHS, _.toLower(_.head(pathParts))) || isHighlightLink(pathParts));
};

/**
 * Extracts Instagram handle from various input formats
 * @param {string} input - Instagram handle, URL, or @ mention
 * @returns {string|null} - Clean Instagram handle or null if invalid
 */
export const extractInstagramHandle = input => {
  if (!input || !_.isString(input)) {
    return null;
  }

  const trimmed = _.trim(input);

  // Check if it looks like a URL (contains protocol or instagram.com)
  const lowerInput = _.toLower(trimmed);
  const looksLikeUrl = lowerInput.includes('://') ||
                       lowerInput.includes('instagram.com') ||
                       lowerInput.includes('www.');

  // If it doesn't look like a URL, treat it as a plain handle
  if (!looksLikeUrl) {
    // Only remove @ prefix - nothing else
    const candidate = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    const validation = validateInstagramHandle(candidate);
    return validation.isValid ? validation.handle : null;
  }

  const { host, pathParts } = parseUrl(trimmed);

  // Check if it's an Instagram domain
  if (!isInstagramHost(host)) {
    // If it's not Instagram, treat as plain handle - only remove @ prefix
    const candidate = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    const validation = validateInstagramHandle(candidate);
    return validation.isValid ? validation.handle : null;
  }

  // Story and app links carry the handle in the second segment (/stories/<handle>/...)
  const firstPart = _.toLower(_.head(pathParts));
  const candidate = _.includes(HANDLE_PREFIX_PATHS, firstPart) ? _.nth(pathParts, 1) : _.head(pathParts);

  // Post, reel and other non-profile links have no handle in them - /p/<code> would
  // otherwise come back as the handle "p"
  if (!candidate || _.includes(NON_PROFILE_PATHS, firstPart) || isHighlightLink(pathParts)) {
    return null;
  }

  const validation = validateInstagramHandle(candidate);
  return validation.isValid ? validation.handle : null;
};

/**
 * Validates Instagram handle against official Instagram rules
 * @param {string} handle - Instagram handle to validate
 * @returns {object} - Validation result with isValid, error, handle, warnings
 */
export const validateInstagramHandle = handle => {
  if (!handle || !_.isString(handle)) {
    return {
      isValid: false,
      error: 'Handle is required',
      handle: null,
    };
  }

  const cleaned = _.toLower(_.trim(handle));

  const validation = {
    isValid: true,
    error: null,
    handle: cleaned,
  };

  // Length check (1-30 characters)
  if (_.size(cleaned) < 1) {
    validation.isValid = false;
    validation.error = 'Username cannot be empty';
    return validation;
  }

  if (_.size(cleaned) > 30) {
    validation.isValid = false;
    validation.error = 'Username cannot be longer than 30 characters';
    return validation;
  }

  // Character validation - only letters, numbers, periods, and underscores
  const validCharRegex = /^[a-z0-9._]+$/;
  if (!validCharRegex.test(cleaned)) {
    validation.isValid = false;
    validation.error = 'Username can only contain letters, numbers, periods, and underscores';
    return validation;
  }

  // Cannot start or end with period
  if (_.startsWith(cleaned, '.') || _.endsWith(cleaned, '.')) {
    validation.isValid = false;
    validation.error = 'Username cannot start or end with a period';
    return validation;
  }

  return validation;
};

// Words a header cell is made of, e.g. "Instagram Handle", "IG_Handle", "instagramUrl",
// "Influencer Name" or Excel's "Column1"
const HEADER_WORDS = [
  'instagram', 'instagramhandle', 'instagramurl', 'insta', 'ig', 'handle', 'handles',
  'username', 'usernames', 'user', 'users', 'url', 'urls', 'link', 'links', 'profile', 'profiles',
  'account', 'accounts', 'name', 'names', 'full', 'influencer', 'influencers', 'creator', 'creators',
  'talent', 'talents', 'email', 'emails', 'social', 'socials', 'tiktok', 'column', 'id',
];

const getFirstCellValue = row => _.trim(String(_.defaultTo(_.head(_.values(row)), '')));

// Splits "IGHandle", "instagram_url" or "Influencer Name" into lowercase words
const getWords = value => _.compact(_.split(
  _.toLower(value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')),
  /[^a-z0-9]+/,
));

// Every word must be a header word, so a handle like "user1" or "the_user_x" stays data.
// Excel's default "Column1" is the one header with a number in it.
const isHeaderText = value => {
  const words = getWords(value);
  return /^column ?\d*$/i.test(value)
    || (!_.isEmpty(words) && _.every(words, word => _.includes(HEADER_WORDS, word)));
};

/**
 * Splits off the header row, if the CSV has one. Files are often exported without a
 * header, and treating their first line as one would silently drop an influencer -
 * so the first row only counts as a header when it is empty or made of header words
 * ("Instagram Handle", "IG_Handle", "Column1"). Anything else, including a typo or a
 * non-Instagram link, stays as data so it is either added or shown as not valid.
 * @param {Array} rows - CSV rows parsed without a header (arrays of cell values)
 * @returns {object} - headerValue (string or null) and the remaining dataRows
 */
export const splitHeaderRow = (rows = []) => {
  if (_.isEmpty(rows)) {
    return { headerValue: null, dataRows: [] };
  }

  const firstValue = getFirstCellValue(_.head(rows));
  const isInstagramLink = _.includes(_.toLower(firstValue), 'instagram.com');

  if (firstValue && (isInstagramLink || !isHeaderText(firstValue))) {
    return { headerValue: null, dataRows: rows };
  }

  return { headerValue: firstValue, dataRows: _.tail(rows) };
};

/**
 * Processes single-column CSV data and extracts/validates Instagram handles
 * @param {Array} csvData - Array of CSV rows, as arrays or objects (single column)
 * @param {number} currentInfluencerCount - Current number of influencers in campaign
 * @param {number} maxInfluencers - Maximum allowed influencers per campaign
 * @param {string[]} existingCampaignHandles - Lowercase Instagram handles already on the campaign
 * @param {number} firstRowNumber - Row number of csvData[0] (2 when a header was split off). Rows
 *   are counted after blank rows are dropped, which matches the file unless it has blank rows
 * @returns {object} - Processed results with validation summary. Each result has a status:
 *   'new', 'already' (on the campaign), 'duplicate' (repeats an earlier row) or 'invalid'
 */
export const processInfluencerCSV = (
  csvData,
  currentInfluencerCount = 0,
  maxInfluencers = 100,
  existingCampaignHandles = [],
  firstRowNumber = 1,
) => {
  const results = _.map(csvData, (row, index) => {
    const result = { originalRow: index + firstRowNumber };

    // Get the first (and only) column value
    const cleanInput = getFirstCellValue(row);

    if (cleanInput) {
      const extractedHandle = extractInstagramHandle(cleanInput);

      if (extractedHandle) {
        const validation = validateInstagramHandle(extractedHandle);

        result.instagramHandle = validation.isValid ? validation.handle : null;
        result.originalInstagramInput = cleanInput;
        result.extractedHandle = extractedHandle;
        result.isValid = validation.isValid;
        result.validationError = validation.error;
      } else {
        result.isValid = false;
        result.validationError = isInstagramNonProfileUrl(cleanInput)
          ? 'Link to a post, not a profile - use the profile link instead'
          : 'Not an Instagram handle or profile link';
        result.originalInstagramInput = cleanInput;
      }
    } else {
      result.isValid = false;
      result.validationError = 'Empty row';
    }

    return result;
  });

  // Check for duplicates
  const validResults = _.filter(results, 'isValid');
  const handleCounts = _.countBy(validResults, 'instagramHandle');
  const duplicates = _.keys(_.pickBy(handleCounts, count => count > 1));

  // Mark handles that are already on the campaign (handles are already lowercase from validateInstagramHandle)
  const existingSet = _.isEmpty(existingCampaignHandles) ? [] : existingCampaignHandles;
  _.forEach(results, result => {
    if (result.isValid && result.instagramHandle) {
      result.alreadyInCampaign = existingSet.indexOf(result.instagramHandle) !== -1;
    }
  });

  // Only the first row for a handle counts - later ones are duplicates
  const seenHandles = {};
  const rowsWithStatus = _.map(results, result => {
    let status = result.alreadyInCampaign ? 'already' : 'new';
    if (!result.isValid) {
      status = 'invalid';
    } else if (seenHandles[result.instagramHandle]) {
      status = 'duplicate';
    } else {
      seenHandles[result.instagramHandle] = true;
    }
    return { ...result, status };
  });

  const uniqueValidInfluencers = _.uniqBy(_.filter(results, 'isValid'), 'instagramHandle');
  const uniqueNewInfluencers = _.reject(uniqueValidInfluencers, r => r.alreadyInCampaign);
  const newToUploadCount = _.size(uniqueNewInfluencers);
  const totalAfterUpload = currentInfluencerCount + newToUploadCount;
  const availableSlots = Math.max(0, maxInfluencers - currentInfluencerCount);

  const summary = {
    total: _.size(csvData),
    valid: _.size(_.filter(results, 'isValid')),
    invalid: _.size(_.filter(results, r => !r.isValid)),
    duplicates: _.size(duplicates),
    overLimit: false,
    currentInfluencerCount,
    maxInfluencers,
    availableSlots,
    alreadyInCampaign: _.size(_.filter(results, r => r.isValid && r.alreadyInCampaign)),
    newToUpload: newToUploadCount,
  };

  if (totalAfterUpload > maxInfluencers) {
    summary.overLimit = true;
    summary.validAfterDedup = newToUploadCount;
    summary.totalAfterUpload = totalAfterUpload;
  }

  return { results: rowsWithStatus, summary, duplicates, uniqueValidInfluencers, uniqueNewInfluencers };
};

/**
 * Test cases for Instagram handle extraction and validation
 */
export const testInstagramHandleExtraction = () => {
  const testCases = [
    'kimkardashian',
    '@kimkardashian',
    'bob.smith', // This should now work correctly
    '@bob.smith',
    'user_name',
    'https://instagram.com/kimkardashian?igsh=N3FzdmJrOHBzejhh',
    'https://instagram.com/kimkardashian/',
    'https://www.instagram.com/kimkardashian?igsh=N3FzdmJrOHBzejhh',
    'instagram.com/kimkardashian',
    'www.instagram.com/kimkardashian',
    'https://instagram.com/kimkardashian/p/somepost/',
    'invalid input',
    '',
    null,
  ];

  console.log('Instagram Handle Extraction Tests:');
  _.forEach(testCases, test => {
    const extracted = extractInstagramHandle(test);
    const validation = extracted ? validateInstagramHandle(extracted) : { isValid: false };
    console.log(`"${test}" -> "${extracted}" (valid: ${validation.isValid})`);
  });
};



/** WEBPACK FOOTER **
 ** ./src/components/csv-upload-modal/instagram-utils.js
 **/